import { Tile } from 'widgets/tiles/components/tile/Tile';
import React, { useRef } from 'react';
import classNames from 'classnames';
import { useTilesConfigSlice } from 'widgets/tiles/store/config/configSlice';
import {
    useTileBackgroundDrop,
    useTilesWidgetTiles,
} from 'widgets/tiles/components/dragAndDrop/hooks';
import { useWidgetContext } from 'widgets/WidgetContext';
import './styles/styles.scss';
import './CapsulesContainer.scss';
import { AddCapsule } from 'widgets/tiles/components/AddCapsule';
import { usePortfolioSelection } from 'portfolio/store/hooks';
import ConfigSettingsModal from 'widgets/tiles/components/tile/ConfigSettingsModal';

import TilesSettings from 'widgets/tiles/components/toolbar/TilesSettings';
import InfoBox from 'utils/components/InfoBox/InfoBox';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { CapsuleContainerWidgetConfigState } from 'widgets/tiles/store/config/CapsuleContainerWidgetConfigState';

/*
 * This is the container for enumeration of Capsules within a Capsule Container Widget
 */
export function CapsulesContainer(props: { openAddCapsulePanel: () => void }) {
    const { openAddCapsulePanel } = props;
    const { widgetKey, height } = useWidgetContext();

    const { symbology } = useSelectAppSettings();

    const { config, dispatchConfigChanged } = useTilesConfigSlice();
    const { orientation, hideToolbar, symbol } = config;

    const { tiles, appendTile, moveTile, removeTile, lastDropped } =
        useTilesWidgetTiles();

    const ref = useRef<HTMLDivElement>(null);

    const { canDrop, isOver, drop } = useTileBackgroundDrop(ref, appendTile);
    drop(ref);

    const containerCssClasses = classNames(
        'capsules-container',
        'TilesContainer',
        `orientation-${orientation}`,
        isOver && canDrop && 'is-dropping'
    );

    const hidingToolbar = tiles.length > 0 && hideToolbar;

    const selection = usePortfolioSelection();
    const selectedSymbol = selection.selectedSymbol;
    const selectedOrders = selection.selectedOrders;
    const selectedSymbols = selection.selectedSymbols.filter(
        (s) => (s ?? '').trim() !== ''
    );
    const haveSymbol = symbol || selectedSymbol;

    const warningPrefix =
        selectedSymbols.length > 0
            ? 'Multiple symbols selected. '
            : selectedOrders.length > 0
            ? `None of your selected orders have ${symbology} symbols. `
            : '';

    let tilesHeight = hidingToolbar ? height - 12 : height - 40;
    if (!haveSymbol) {
        tilesHeight = tilesHeight - 28;
    }
    const style = { height: tilesHeight };

    return (
        <>
            {!haveSymbol && (
                <SymbolWarning
                    prefix={warningPrefix}
                    dispatchConfigChanged={dispatchConfigChanged}
                />
            )}
            <div className={containerCssClasses} ref={ref} style={style}>
                {tiles.map((tile, index) => (
                    <Tile
                        disableSymbolConfig={true}
                        index={index}
                        widgetId={tile}
                        key={tile.widgetKey}
                        moveTile={moveTile}
                        removeTile={() => removeTile(tile.widgetKey)}
                        tilesWidgetKey={widgetKey}
                        className={
                            lastDropped?.widgetKey === tile.widgetKey
                                ? 'dropped'
                                : ''
                        }
                    />
                ))}
                <AddCapsule openAddCapsulePanel={openAddCapsulePanel} />
                <div className="dummy-drop-tile" />
            </div>
        </>
    );
}

function SymbolWarning(props: {
    prefix?: string;
    dispatchConfigChanged: (
        delta: Partial<CapsuleContainerWidgetConfigState>
    ) => void;
}) {
    const { dispatchConfigChanged, prefix } = props;

    return (
        <InfoBox
            className="tiles-container-selection-warning"
            info={
                <span>
                    {prefix}Select a symbol or specify one in the&nbsp;
                    <ConfigSettingsModal
                        dispatcher={dispatchConfigChanged}
                        triggerText={`Capsule Container Settings`}
                        headerText={`Capsule Container Settings`}
                        renderSettings={(p) => <TilesSettings {...p} />}
                    />
                    .
                </span>
            }
        />
    );
}
